import { formatWeekday, t } from "@hakili/i18n";
import { Button, Modal } from "@hakili/ui";
import { useRef, useState, type ReactElement } from "react";

import type { DoorResult } from "../session/useSession.js";

/**
 * `P1-DLW-048` (G-E1), shared with the Accueil (the lead task « Confirmer la réception » opens the very same dialog): **the one confirmation the dealer's receipt asks for.**
 */
/** The one question the confirmation asks: the day the dealer received his share. One dialog, one `Idempotency-Key` (held by the caller). */
export function ReceiptDialog(props: {
  readonly today: string | null;
  readonly onCancel: () => void;
  readonly onConfirm: (receivedOn: string) => Promise<DoorResult<unknown>>;
  readonly onDone: () => void;
}): ReactElement {
  const [day, setDay] = useState(props.today ?? "");
  const [error, setError] = useState<string | null>(null);
  const busy = useRef(false);
  const [working, setWorking] = useState(false);
  const valid = day !== "" && (props.today === null || day <= props.today);

  const confirm = async (): Promise<void> => {
    if (busy.current || !valid) return;
    busy.current = true;
    setWorking(true);
    setError(null);
    const answer = await props.onConfirm(day);
    busy.current = false;
    setWorking(false);
    if (answer.ok) {
      props.onDone();
      return;
    }
    setError(answer.message);
  };

  return (
    <Modal
      open
      title={<span data-testid="dealer-dossiers-recu-titre">{t("dealer.dossiers.recuTitre")}</span>}
      onClose={props.onCancel}
      closeLabel={t("common.action.annuler")}
    >
      <div>
        <input
          type="date"
          value={day}
          max={props.today ?? undefined}
          aria-label={t("dealer.dossiers.recuTitre")}
          onChange={(event) => {
            setDay(event.target.value);
          }}
          data-testid="dealer-dossiers-recu-date"
          className="block rounded-lg border border-gray-200 px-3 py-2 text-sm"
        />
        {
          /*
           * The designer's change: the native field draws the day in the BROWSER's order (« 11/02/2026 », month first), which in Mali reads as 11 February.
           * The chosen day is said in words under it through the catalogue's own date (`formatWeekday`, `@hakili/i18n`: « lundi 2 novembre 2026 », « 1er » on the first).
           */
          day === "" ? null : (
            <p
              className="m-0 mt-2 text-sm text-gray-700"
              aria-live="polite"
              data-testid="dealer-dossiers-recu-en-mots"
            >
              {formatWeekday(new Date(`${day}T12:00:00Z`))}
            </p>
          )
        }
        {error === null ? null : (
          <p role="alert" className="mt-3 text-sm text-gray-700" data-testid="dealer-dossiers-recu-erreur">
            {error}
          </p>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={props.onCancel} data-testid="dealer-dossiers-recu-annuler">
            {t("common.action.annuler")}
          </Button>
          <Button
            variant="primary"
            disabled={working || !valid}
            onClick={() => {
              void confirm();
            }}
            data-testid="dealer-dossiers-recu-valider"
          >
            {t("dealer.dossiers.recuConfirmer")}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
